import { useEffect } from 'react';

export interface SEOProps {
    title: string;
    description: string;
    canonicalUrl?: string;
    ogImage?: string;
    ogType?: 'website' | 'article' | 'music' | 'profile';
    twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
    lang?: string;
    author?: string;
    keywords?: string[];
    structuredData?: Record<string, unknown>;
    noIndex?: boolean;
}

/**
 * Utility function to update document title
 */
export const updateTitle = (title: string, siteName = 'Too Many Mornings') => {
    document.title = `${title} | ${siteName}`;
};

/**
 * Utility function to update meta description
 */
export const updateMetaDescription = (description: string) => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
        metaDescription.setAttribute('content', description);
    } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = description;
        document.head.appendChild(meta);
    }
};

/**
 * Utility function to update canonical URL
 */
export const updateCanonicalUrl = (url: string) => {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (link) {
        link.href = url;
    } else {
        link = document.createElement('link');
        link.rel = 'canonical';
        link.href = url;
        document.head.appendChild(link);
    }
};

/**
 * Utility function to update Open Graph metadata
 */
export const updateOpenGraph = (
    title: string,
    description: string,
    url: string,
    image?: string,
    type: 'website' | 'article' | 'music' | 'profile' = 'website'
) => {
    const metaTags = {
        'og:title': title,
        'og:description': description,
        'og:url': url,
        'og:type': type,
        ...(image && { 'og:image': image }),
    };

    Object.entries(metaTags).forEach(([property, content]) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (meta) {
            meta.setAttribute('content', content);
        } else {
            meta = document.createElement('meta');
            meta.setAttribute('property', property);
            meta.setAttribute('content', content);
            document.head.appendChild(meta);
        }
    });
};

/**
 * Utility function to update Twitter Card metadata
 */
export const updateTwitterCard = (
    title: string,
    description: string,
    image?: string,
    cardType: 'summary' | 'summary_large_image' | 'app' | 'player' = 'summary_large_image'
) => {
    const metaTags = {
        'twitter:card': cardType,
        'twitter:title': title,
        'twitter:description': description,
        ...(image && { 'twitter:image': image }),
    };

    Object.entries(metaTags).forEach(([name, content]) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (meta) {
            meta.setAttribute('content', content);
        } else {
            meta = document.createElement('meta');
            meta.setAttribute('name', name);
            meta.setAttribute('content', content);
            document.head.appendChild(meta);
        }
    });
};

/**
 * Utility function to add/update structured data
 */
export const updateStructuredData = (data: Record<string, unknown>) => {
    // Remove any existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
        existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
};

/**
 * Utility function to control indexing
 */
export const setNoIndex = (noIndex: boolean) => {
    let meta = document.querySelector('meta[name="robots"]');
    if (meta) {
        meta.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
    } else {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        meta.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
        document.head.appendChild(meta);
    }
};

/**
 * React hook to update SEO metadata
 */
export const useSEO = ({
    title,
    description,
    canonicalUrl,
    ogImage,
    ogType = 'website',
    twitterCard = 'summary_large_image',
    lang = 'en',
    author,
    keywords,
    structuredData,
    noIndex = false,
}: SEOProps) => {
    useEffect(() => {
        // Update document language
        document.documentElement.lang = lang;

        // Update title
        updateTitle(title);

        // Update meta description
        updateMetaDescription(description);

        // Update canonical URL if provided
        if (canonicalUrl) {
            updateCanonicalUrl(canonicalUrl);
        }

        // Update Open Graph metadata
        updateOpenGraph(
            title,
            description,
            canonicalUrl || window.location.href,
            ogImage,
            ogType
        );

        // Update Twitter Card metadata
        updateTwitterCard(title, description, ogImage, twitterCard);

        // Update author metadata if provided
        if (author) {
            let meta = document.querySelector('meta[name="author"]');
            if (meta) {
                meta.setAttribute('content', author);
            } else {
                meta = document.createElement('meta');
                meta.setAttribute('name', 'author');
                meta.setAttribute('content', author);
                document.head.appendChild(meta);
            }
        }

        // Update keywords if provided
        if (keywords && keywords.length > 0) {
            let meta = document.querySelector('meta[name="keywords"]');
            if (meta) {
                meta.setAttribute('content', keywords.join(', '));
            } else {
                meta = document.createElement('meta');
                meta.setAttribute('name', 'keywords');
                meta.setAttribute('content', keywords.join(', '));
                document.head.appendChild(meta);
            }
        }

        // Update structured data if provided
        if (structuredData) {
            updateStructuredData(structuredData);
        }

        // Update indexing instructions
        setNoIndex(noIndex);

    }, [
        title,
        description,
        canonicalUrl,
        ogImage,
        ogType,
        twitterCard,
        lang,
        author,
        keywords,
        structuredData,
        noIndex,
    ]);
};

/**
 * React component for SEO
 */
export const SEO = (props: SEOProps) => {
    useSEO(props);
    return null;
};

export default SEO; 